import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'

import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import { styled } from 'styled-components'

import {
  useAuthenticatedPerson,
  useAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { paymentEntitiesQuery } from '@b-stock/fe-core/bstock-next/queries'
import {
  Banner,
  Button,
  FormattedMessage,
  Modal,
  showSnackbar,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'
import type { BillingDetails } from '@b-stock/fe-core/pay-ui'
import { PayMethodTypeEnum } from '@b-stock/payments-methods-api-client'

import { useAutoPaymentMethods } from '@/buyer-portal/components/features/listing/hooks/useAutoPaymentMethods'
import { derivePaymentMode } from '@/buyer-portal/components/features/order/checkout/helpers/utils'
import { BalanceWidgetContainer } from '@/buyer-portal/components/features/order/checkout/PaymentMethods/helpers/BalanceWidgetContainer'
import { initializeBalanceWidget } from '@/buyer-portal/components/features/order/checkout/PaymentMethods/helpers/initializeBalanceWidget'
import { showPaymentMethodAddedSnackbar } from '@/buyer-portal/components/features/order/checkout/PaymentMethods/helpers/showPaymentMethodAddedSnackbar'
import { useCardAddScope } from '@/buyer-portal/components/features/order/checkout/PaymentMethods/helpers/useCardAddScope'
import { PaymentRestrictionBanner } from '@/buyer-portal/components/features/order/checkout/PaymentMethods/PaymentRestrictionBanner'
import {
  isAchMethod,
  isPaymentMethodDisabled,
} from '@/buyer-portal/components/features/order/checkout/PaymentMethods/utils'
import { getAppConfig } from '@/buyer-portal/config/appConfig'
import useAccount from '@/buyer-portal/helpers/useAccount'
import { useAchSettlementMode } from '@/buyer-portal/helpers/useAchSettlementMode'
import { isWireRefused } from '@/buyer-portal/helpers/useIsSellerCollectsSale'
import { useListingPaymentRestrictions } from '@/buyer-portal/helpers/useListingPaymentRestrictions'
import { usePaymentRestrictions } from '@/buyer-portal/helpers/usePaymentRestrictions'
import deletePaymentMethod from '@/buyer-portal/mutations/deletePaymentMethod'
import getAllPaymentMethods from '@/buyer-portal/mutations/getAllPaymentMethods'
import setPaymentMethodDefault from '@/buyer-portal/mutations/setPaymentMethodDefault'
import startNetTermsQualification from '@/buyer-portal/mutations/startNetTermsQualification'
import { AchProcessingNotice } from '@/components/AchProcessingNotice'
import { AddBuyerPaymentMethodModal } from '@/components/payments/AddBuyerPaymentMethodModal/AddBuyerPaymentMethodModal'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useHandleBalanceCCAdded } from '@/helpers/useHandleBalanceCCAdded'

import { getSellerCollectsFeeAccess } from '../sellerCollectsFeeAccess'
import { AutoPaymentMethodList } from './AutoPaymentMethodList'
import { useHandlePaymentAdded } from './hooks/useHandlePaymentAdded'
import {
  SellerCollectsFeeCardList,
  selectFeeCards,
} from './SellerCollectsFeeCardList'
import type { PaymentMethodSelectionModalProps } from './types'
import { filterAutoPaySupportedMethods } from './utils'

const StyledModal = styled(Modal)`
  position: fixed;
  right: 0;
  top: 0;
  height: 100vh;
  width: 50vw;
  max-width: 50rem;
  margin: 0;
  transform: none;
  border-radius: 0;

  @media ${Breakpoints.max.medium} {
    width: 100vw;
  }
`

// FF autoPayEnforced: tighter scroll region for content inside BidModal
const ModalContent = styled(Modal.Content)`
  padding: 1.5rem 3rem;
  min-height: 25.625rem;
  max-height: 60vh;
  overflow-y: auto;
  scrollbar-gutter: stable;
`

// Legacy (FF autoPayEnforced off): original taller scroll region for standalone right-side panel
const LegacyModalContent = styled(Modal.Content)`
  padding: 1.5rem 3rem;
  min-height: 15rem;
  overflow-y: auto;
`

// FF autoPayEnforced: bare wrapper, rendered inside BidModal as content swap
const ModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const BackHeaderContent = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: 1;
`
// FF autoPayEnforced: new styled components
const PaymentHeader = styled.div`
  ${Typography.Title3}
  color: ${Colors.Semantic.Neutral.black};
  margin-bottom: 0.25rem;
`

const PaymentSubtext = styled.div`
  ${Typography.Body1}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  margin-bottom: 1.5rem;
`

const StyledIcon = styled(FontAwesomeIcon)`
  font-size: 1rem;
  cursor: pointer;
  margin: 0 1.5rem 0 0;
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const GracePeriodExpiredBanner = styled(Banner)`
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
`

// Sits between the charge message and the method list, so it clears the
// subtext above it as well as the list below.
const AchProcessingBanner = styled(AchProcessingNotice)`
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
`

const ns = 'BidConfirmationModal.autoPay.modal'

/**
 * Payment method selection component.
 *
 * When renderActions is provided (auto pay FF-on), renders as content inside the
 * parent BidModal (ModalWrapper) with back chevron header and custom actions.
 *
 * When renderActions is not provided (FF-off / legacy), renders as a
 * standalone right-side panel (StyledModal) with built-in Confirm/Cancel buttons.
 */
export const PaymentMethodSelectionModal = ({
  isOpen,
  onClose,
  onConfirm,
  accountId,
  selectedPaymentMethodId,
  payableAmountInCents,
  autoOpenAdd,
  onProcessingChange,
  renderActions,
  listingIsCreditCardEnabled,
  onWireConfirm,
  initialIsWireSelected = false,
  isAutoPayProcessing = false,
  currency,
  isSellerCollects = false,
  saleKind = 'bid',
  startWithCardForm = false,
}: PaymentMethodSelectionModalProps) => {
  const { trackButtonClicked } = useAnalytics()
  const queryClient = useQueryClient()
  const accessToken = useAccessToken() || ''
  const person = useAuthenticatedPerson()
  const account = useAccount()
  const appConfig = getAppConfig()
  const enableSelectionFlow = useFlag<boolean>(
    FeatureFlags.ENABLE_BALANCE_CREDIT_CARDS
  )

  // Deliberately without the Seller Collects argument. That branch of the hook
  // blocks card, ACH and net terms, which is right where the buyer is paying
  // the seller for goods — but this picker collects the B-Stock fee, and those
  // are the methods the fee is paid with. Passing it here disabled every
  // method and left Confirm permanently dead.
  const { isCreditCardBlocked, isACHBlocked, isNetTermsBlocked } =
    useListingPaymentRestrictions(listingIsCreditCardEnabled)
  const autoPayEnforced = useFlag(FeatureFlags.AUTOPAY_ENFORCED)

  // Use account-level restrictions only for the banner — listing-level CC config
  // (listingIsCreditCardEnabled) is not an account restriction and should not trigger it
  const accountRestrictions = usePaymentRestrictions()
  // Product (2026-10-30): on Seller Collects the B-Stock fee is charged by
  // credit card only. ACH, Net Terms and wire are not rendered, and adding a
  // method opens the card form directly, never the Add New drawer.
  //
  // Not gated on the listing's isCreditCardEnabled: the fee is invoiced
  // through Balance, not the listing checkout, so only an account-level card
  // block counts (PROD Seller Collects listings are card-disabled). The one
  // rule, shared with the fee sections, lives in getSellerCollectsFeeAccess.
  const isFeeCardBlocked = accountRestrictions.isCreditCardBlocked
  const feeAccess = useMemo(
    () =>
      getSellerCollectsFeeAccess({
        isSellerCollects,
        enableBalanceCreditCards: enableSelectionFlow,
        currency,
        isAccountCardBlocked: isFeeCardBlocked,
      }),
    [isSellerCollects, enableSelectionFlow, currency, isFeeCardBlocked]
  )
  const { isFeeCardOnly } = feeAccess
  const isNonUsdCurrency = feeAccess.isNonUsd
  // The card block the picker enforces: the account's alone on the fee
  // picker, the listing-merged one everywhere else.
  const pickerCardBlocked = isFeeCardOnly
    ? isFeeCardBlocked
    : isCreditCardBlocked
  // The fee section's "Add a card" goes straight to the card form: the
  // picker opens only to host the card widget, and never where the account
  // blocks cards (there is nothing to add there).
  const startsOnCardForm =
    startWithCardForm && isFeeCardOnly && feeAccess.canAddCard
  const showAchUnavailable =
    accountRestrictions.isACHBlocked && !isNonUsdCurrency && !isFeeCardOnly
  // Wire is the one method that does not apply: it pays for goods, and on a
  // Seller Collects listing B-Stock is not collecting for those (PRD 4.3). The
  // account may well be entitled to it; this listing still is not.
  //
  // Offered at all, rather than merely not preselected — the fallback flag
  // below only drives the automatic choice, so gating that alone still left
  // the radio there to be clicked by hand.
  //
  // Through the same helper as AutoPayMethodProvider, so the picker cannot
  // refuse a method the provider has already selected.
  const wireSelectable =
    !!onWireConfirm &&
    !isWireRefused({ isSellerCollectsSale: isSellerCollects, currency })
  const canUseWireFallback =
    autoPayEnforced &&
    wireSelectable &&
    accountRestrictions.isWireFallbackAvailable === true
  const hasPaymentRestriction =
    autoPayEnforced &&
    !isFeeCardOnly &&
    (accountRestrictions.isCreditCardBlocked ||
      accountRestrictions.isNetTermsBlocked)

  // listingIsCreditCardEnabled is intentionally omitted: this call only consumes
  // paymentMethods/isLoading/refetch, none of which depend on it. If defaultPaymentMethod
  // is ever consumed here, listingIsCreditCardEnabled must be passed too.
  const {
    isLoading,
    refetch: refetchPaymentMethods,
    paymentMethods: queryPaymentMethods,
  } = useAutoPaymentMethods({
    accountId,
    enabled: isOpen && !!accountId,
  })

  const paymentMethods = queryPaymentMethods
  const supportedPaymentMethods = useMemo(
    () => filterAutoPaySupportedMethods(paymentMethods),
    [paymentMethods]
  )
  const feeCards = useMemo(
    () => selectFeeCards(supportedPaymentMethods, feeAccess),
    [supportedPaymentMethods, feeAccess]
  )

  const [internalSelection, setInternalSelection] = useState<string | null>(
    selectedPaymentMethodId ?? null
  )
  // On the fee picker only a listed card can be confirmed: a carried-in ACH
  // or Net Terms id is not rendered, so it must not enable Confirm either.
  const selection =
    isFeeCardOnly && !feeCards.some((card) => card.id === internalSelection)
      ? null
      : internalSelection
  // A wire choice carried in from a previous open is ignored here for the
  // same reason: the listing is what makes wire inapplicable, not the buyer.
  const [isWireSelected, setIsWireSelected] = useState(
    wireSelectable && initialIsWireSelected
  )
  const hasExplicitSelection = useRef(false)

  const achSettlementMode = useAchSettlementMode()

  // Keyed off the live in-dialog selection rather than the id the dialog opened
  // with, so switching to a card or wire drops the banner without a reopen.
  const showAchProcessingBanner = useMemo(() => {
    if (
      achSettlementMode !== 'settled' ||
      isWireSelected ||
      isACHBlocked ||
      isNonUsdCurrency
    )
      return false
    const selectedMethod = supportedPaymentMethods.find(
      (pm) => pm.id === selection
    )
    return selectedMethod !== undefined && isAchMethod(selectedMethod.type)
  }, [
    achSettlementMode,
    isACHBlocked,
    isNonUsdCurrency,
    isWireSelected,
    selection,
    supportedPaymentMethods,
  ])

  const achProcessingBanner = showAchProcessingBanner ? (
    <AchProcessingBanner>
      <FormattedMessage
        id={`${ns}.achProcessingBanner`}
        values={{ b: (chunks: ReactNode) => <strong>{chunks}</strong> }}
      />
    </AchProcessingBanner>
  ) : null

  const [addMethodModalOpen, setAddMethodModalOpen] = useState(!!autoOpenAdd)
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false)
  const [isBalanceWidgetActive, setIsBalanceWidgetActive] = useState(false)
  const [cardFormStarted, setCardFormStarted] = useState(false)
  const [isStartingQualification, setIsStartingQualification] = useState(false)
  // auto-selecting newly added payment method
  const [newlyAddedMethodId, setNewlyAddedMethodId] = useState<string | null>(
    null
  )

  // Sync internal selection with prop and refresh payment methods when modal opens
  useEffect(() => {
    if (!isOpen) {
      hasExplicitSelection.current = false
      return
    }
    if (!hasExplicitSelection.current) {
      setIsWireSelected(wireSelectable && initialIsWireSelected)
      setInternalSelection(selectedPaymentMethodId ?? null)
      void queryClient.invalidateQueries({
        queryKey: [paymentEntitiesQuery.name],
      })
    }
  }, [
    isOpen,
    selectedPaymentMethodId,
    initialIsWireSelected,
    wireSelectable,
    queryClient,
  ])

  // Resolve only this dialog's draft selection; the bid/Buy Now provider owns
  // the initial selection on those surfaces. Never overwrite a usable choice.
  useEffect(() => {
    if (!isOpen || isLoading || accountRestrictions.isAccountLoading) return
    const selectedMethod = supportedPaymentMethods.find(
      (pm) => pm.id === internalSelection
    )
    const selectedAchBlocked =
      selectedMethod && isAchMethod(selectedMethod.type) && isACHBlocked
    if (selectedAchBlocked) {
      setInternalSelection(null) // oxlint-disable-line react/set-state-in-effect
      if (canUseWireFallback) setIsWireSelected(true)
    } else if (
      !internalSelection &&
      !isWireSelected &&
      !hasExplicitSelection.current &&
      canUseWireFallback
    ) {
      setIsWireSelected(true)
    } else if (
      autoPayEnforced &&
      selectedMethod &&
      isPaymentMethodDisabled(selectedMethod.type, {
        ach: isACHBlocked || isNonUsdCurrency,
        card: pickerCardBlocked || isNonUsdCurrency,
        netTerms: isNetTermsBlocked || isNonUsdCurrency,
      })
    ) {
      setInternalSelection(null)
    }
  }, [
    isOpen,
    isLoading,
    accountRestrictions.isAccountLoading,
    internalSelection,
    isWireSelected,
    supportedPaymentMethods,
    isACHBlocked,
    pickerCardBlocked,
    isNetTermsBlocked,
    isNonUsdCurrency,
    canUseWireFallback,
    autoPayEnforced,
  ])

  useEffect(() => {
    if (
      newlyAddedMethodId &&
      paymentMethods.some((pm) => pm.id === newlyAddedMethodId)
    ) {
      // autoOpenAdd, or a card added straight from the fee section:
      // auto-confirm with the new method and close
      if (autoOpenAdd || startsOnCardForm) {
        const newMethod = paymentMethods.find(
          (pm) => pm.id === newlyAddedMethodId
        )
        if (newMethod) {
          setNewlyAddedMethodId(null) // oxlint-disable-line react/set-state-in-effect
          onConfirm(newMethod.id, newMethod)
          return
        }
      }
      hasExplicitSelection.current = true
      setIsWireSelected(false)
      setInternalSelection(newlyAddedMethodId)
      setNewlyAddedMethodId(null)
    }
  }, [
    newlyAddedMethodId,
    paymentMethods,
    autoOpenAdd,
    startsOnCardForm,
    onConfirm,
  ])

  // FF autoPayEnforced: auto-set first PM as account default (matches checkout behavior)
  // When there's exactly 1 regular PM without primaryAt, call the API to set it as primary
  const regularMethods = useMemo(
    () =>
      supportedPaymentMethods.filter(
        (pm) => pm.type !== PayMethodTypeEnum.Getbalance
      ),
    [supportedPaymentMethods]
  )
  const singleMethodId =
    regularMethods.length === 1 ? regularMethods[0]?.id : null
  const shouldSetDefault =
    autoPayEnforced &&
    regularMethods.length === 1 &&
    !regularMethods[0]?.primaryAt
  const processedMethodIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (
      shouldSetDefault &&
      singleMethodId &&
      !processedMethodIdsRef.current.has(singleMethodId)
    ) {
      processedMethodIdsRef.current.add(singleMethodId)

      void setPaymentMethodDefault(singleMethodId, accessToken)
        .then(() => {
          void refetchPaymentMethods()
          showSnackbar({
            content: (
              <FormattedMessage id="CheckoutModal.add.paymentMethod.primary" />
            ),
            options: { autoClose: true },
          })
        })
        .catch((error: unknown) => {
          console.error('Failed to set default payment method', error)
          showSnackbar({
            content: (
              <FormattedMessage id={`${ns}.add.paymentMethod.error.content`} />
            ),
            options: { autoClose: true, type: 'error' },
          })
        })
    }
  }, [accessToken, singleMethodId, shouldSetDefault, refetchPaymentMethods])

  const handleSelect = useCallback((paymentMethodId: string) => {
    hasExplicitSelection.current = true
    setInternalSelection(paymentMethodId)
    setIsWireSelected(false)
  }, [])

  const handleWireSelect = useCallback(() => {
    hasExplicitSelection.current = true
    setIsWireSelected(true)
    setInternalSelection(null)
  }, [])

  const handleConfirm = useCallback(() => {
    if (isWireSelected) {
      trackButtonClicked({
        buttonName: 'confirm_payment_method',
        payment_mode: derivePaymentMode(undefined, true),
      })
      onWireConfirm?.()
      return
    }

    if (!selection) return

    const selectedMethod = supportedPaymentMethods.find(
      (pm) => pm.id === selection
    )

    if (selectedMethod) {
      trackButtonClicked({
        buttonName: 'confirm_payment_method',
        payment_mode: derivePaymentMode(selectedMethod.type),
      })
      onConfirm(selection, selectedMethod)
    }
  }, [
    isWireSelected,
    onWireConfirm,
    selection,
    supportedPaymentMethods,
    onConfirm,
    trackButtonClicked,
  ])

  const handleCancel = useCallback(() => {
    if (renderActions) {
      trackButtonClicked({ buttonName: 'cancel_payments_autocharge' })
    }
    setInternalSelection(selectedPaymentMethodId ?? null)
    setIsWireSelected(false)
    onClose()
  }, [onClose, selectedPaymentMethodId, renderActions, trackButtonClicked])

  const handleAddNew: () => void = useCallback(() => {
    setAddMethodModalOpen(true)
  }, [])

  const handleApplyForNetTerms = useCallback(async () => {
    setIsStartingQualification(true)
    try {
      const result = await startNetTermsQualification(
        {
          buyerAccountId: accountId,
          postActivationRedirectUrl: `${window.location.origin}${window.location.pathname}`,
        },
        accessToken
      )
      window.open(result.link, '_blank')
    } catch (error) {
      console.error('Error applying for Net Terms:', error)
      showSnackbar({
        content: <FormattedMessage id={`${ns}.netTerms.error`} />,
        options: { type: 'error', autoClose: true },
      })
    } finally {
      setIsStartingQualification(false)
    }
  }, [accountId, accessToken])

  const handlePaymentAdded = useHandlePaymentAdded({
    accountId,
    accessToken,
    paymentMethods,
    refetchPaymentMethods,
    setAddMethodModalOpen,
    setPaymentMethodsLoading,
    setNewlyAddedMethodId,
  })

  const cardAddScope = useCardAddScope(accountId, accessToken)

  const { handleBalanceCCAdded, isProcessing: balanceCCProcessing } =
    useHandleBalanceCCAdded({
      scopeKey: cardAddScope.scopeKey,
      existingMethods: paymentMethods,
      fetchMethods: () =>
        getAllPaymentMethods(accountId, cardAddScope.getAccessToken()),
      deleteMethod: (id) =>
        deletePaymentMethod(id, cardAddScope.getAccessToken()),
      onSuccess: async (newMethod) => {
        await queryClient.invalidateQueries({
          queryKey: [paymentEntitiesQuery.name],
        })
        setNewlyAddedMethodId(newMethod.id)
        trackButtonClicked({
          buttonName: 'added_new_payment',
          payment_mode: 'CREDIT_CARD',
        })
        if (!showPaymentMethodAddedSnackbar(newMethod, { autoClose: true })) {
          showSnackbar({
            content: (
              <FormattedMessage
                id={`${ns}.add.paymentMethod.warning.content`}
              />
            ),
            options: { autoClose: true, type: 'error' },
          })
        }
      },
      onDuplicate: () => {
        showSnackbar({
          content: (
            <FormattedMessage
              id={`${ns}.add.paymentMethod.duplicate.content`}
            />
          ),
          options: { autoClose: true, type: 'error' },
        })
      },
      onTimeout: () => {
        showSnackbar({
          content: (
            <FormattedMessage id={`${ns}.add.paymentMethod.error.content`} />
          ),
          options: { autoClose: true, type: 'error' },
        })
      },
      pollConfig: { maxRetries: 10, retryDelay: 2000, initialDelay: 4000 },
    })

  const isProcessingPaymentMethod = paymentMethodsLoading || balanceCCProcessing

  // autoOpenAdd: close when add modal is dismissed and not processing
  // (handles cancel, duplicate, and error cases)
  useEffect(() => {
    if (
      autoOpenAdd &&
      !addMethodModalOpen &&
      !isProcessingPaymentMethod &&
      !isBalanceWidgetActive
    ) {
      onClose()
    }
  }, [
    autoOpenAdd,
    addMethodModalOpen,
    isProcessingPaymentMethod,
    isBalanceWidgetActive,
    onClose,
  ])

  // Notify parent of processing state changes
  useEffect(() => {
    onProcessingChange?.(isProcessingPaymentMethod || isBalanceWidgetActive)
  }, [isProcessingPaymentMethod, isBalanceWidgetActive, onProcessingChange])

  const handleBalanceWidgetSuccess = useCallback(
    (paymentMethodId: string) => {
      setIsBalanceWidgetActive(false)
      void handleBalanceCCAdded(paymentMethodId)
    },
    [handleBalanceCCAdded]
  )

  const handleBalanceWidgetError = useCallback((_error?: unknown) => {
    setIsBalanceWidgetActive(false)
    showSnackbar({
      content: (
        <FormattedMessage id={`${ns}.add.paymentMethod.error.content`} />
      ),
      options: { autoClose: true, type: 'error' },
    })
  }, [])

  const handleBalanceWidgetDismissed = useCallback(() => {
    setIsBalanceWidgetActive(false)
  }, [])

  const handleCreditCardSelected = useCallback(() => {
    setIsBalanceWidgetActive(true)
    void initializeBalanceWidget({
      isCurrent: cardAddScope.isCurrent,
      getAccessToken: cardAddScope.getAccessToken,
      buyerAccountId: accountId,
      accessToken,
      onSuccess: handleBalanceWidgetSuccess,
      onError: handleBalanceWidgetError,
      onClose: handleBalanceWidgetDismissed,
      onCancel: handleBalanceWidgetDismissed,
    })
  }, [
    accountId,
    accessToken,
    handleBalanceWidgetSuccess,
    handleBalanceWidgetError,
    handleBalanceWidgetDismissed,
    cardAddScope,
  ])

  // Opens the card form once per opening, after the widget container below
  // has mounted and the account's card block is known.
  useEffect(() => {
    if (!isOpen) {
      setCardFormStarted(false) // oxlint-disable-line react/set-state-in-effect
      return
    }
    if (
      !startsOnCardForm ||
      cardFormStarted ||
      accountRestrictions.isAccountLoading
    )
      return
    setCardFormStarted(true)
    handleCreditCardSelected()
  }, [
    isOpen,
    startsOnCardForm,
    cardFormStarted,
    accountRestrictions.isAccountLoading,
    handleCreditCardSelected,
  ])

  if (!isOpen) return null

  // No list step on the way to the card form; once the buyer leaves the
  // form without a card, the list (and its no-card banner) is there.
  const hideMethodList =
    startsOnCardForm && (!cardFormStarted || isBalanceWidgetActive)

  const isConfirmDisabled =
    (!selection && !isWireSelected) ||
    isLoading ||
    accountRestrictions.isAccountLoading === true ||
    isProcessingPaymentMethod ||
    isAutoPayProcessing

  const billingDetails: BillingDetails = {
    name: account.data?.business?.name || 'Unnamed Business',
    email: person?.email || '',
  }

  // Never on the fee picker: the drawer offers ACH, whose consent covers "the
  // amount due on your purchases" — the card form is opened directly instead.
  const addPaymentMethodModal = addMethodModalOpen && !isFeeCardOnly && (
    <AddBuyerPaymentMethodModal
      setModalOpen={setAddMethodModalOpen}
      onDone={handlePaymentAdded}
      onCancel={() => setAddMethodModalOpen(false)}
      accountId={accountId}
      appConfig={appConfig}
      getAccessToken={() => Promise.resolve(accessToken)}
      billingDetails={billingDetails}
      buyerAccountId={accountId}
      onCreditCardSelected={handleCreditCardSelected}
      onBalanceSuccess={handleBalanceWidgetSuccess}
      onBalanceError={handleBalanceWidgetError}
      enableSelectionFlow={enableSelectionFlow && !isCreditCardBlocked}
      achEnabled={!isACHBlocked}
      // Never on Seller Collects: AutoPay excludes these orders, so the
      // drawer's charge-timing line describes a charge that cannot happen.
      autoPayEnabled={autoPayEnforced && !isSellerCollects}
    />
  )

  const listIsLoading = isLoading || isProcessingPaymentMethod
  const sharedListProps = {
    paymentMethods: supportedPaymentMethods,
    selectedPaymentMethodId: internalSelection,
    onSelect: handleSelect,
    onAddNew: handleAddNew,
    onApplyForNetTerms: handleApplyForNetTerms,
    isStartingQualification,
    payableAmountInCents,
    isLoading: listIsLoading,
    listingIsCreditCardEnabled,
    onWireSelect: wireSelectable ? handleWireSelect : undefined,
    isWireSelected,
    currency,
  }
  // Only rendered where isFeeCardOnly: the layouts below branch on it
  // explicitly, so nothing can bring the full picker back on Seller Collects.
  const feeCardList = hideMethodList ? null : (
    <SellerCollectsFeeCardList
      feeCards={feeCards}
      selectedPaymentMethodId={selection}
      onSelect={handleSelect}
      onAddCard={handleCreditCardSelected}
      isLoading={listIsLoading}
      isAccountCardBlocked={!feeAccess.canAddCard}
      saleKind={saleKind}
    />
  )

  // autoOpenAdd: skip PM list entirely, render only AddPaymentMethodModal
  if (autoOpenAdd) {
    return <>{addPaymentMethodModal}</>
  }

  // FF autoPayEnforced: content swap inside parent BidModal
  if (renderActions) {
    return (
      <>
        <ModalWrapper data-testid="payment-method-selection-modal">
          <Modal.Header>
            <BackHeaderContent>
              <StyledIcon
                icon={faChevronLeft}
                onClick={onClose}
                role="button"
                tabIndex={0}
              />
              <FormattedMessage id={`${ns}.header.enforced`} />
            </BackHeaderContent>
          </Modal.Header>

          <ModalContent>
            <PaymentHeader>
              <FormattedMessage id={`${ns}.payment.label`} />
            </PaymentHeader>
            <PaymentSubtext data-testid="payment-charge-message">
              <FormattedMessage
                id={
                  isSellerCollects
                    ? 'BidConfirmationModal.sellerCollects.pickerChargeMessage'
                    : `${ns}.payment.chargeMessage`
                }
              />
            </PaymentSubtext>
            {showAchUnavailable && (
              <Banner type="info" hideIcon>
                <FormattedMessage id="BlockedAchPicker.message" />
              </Banner>
            )}
            {hasPaymentRestriction && (
              <PaymentRestrictionBanner
                suppressAddMethodSuggestion={accountRestrictions.isACHBlocked}
              />
            )}
            {achProcessingBanner}
            {isFeeCardOnly ? (
              feeCardList
            ) : (
              <AutoPaymentMethodList
                {...sharedListProps}
                onCreditCardSelected={handleCreditCardSelected}
              />
            )}
            <BalanceWidgetContainer id="balance-sdk" />
            {isAutoPayProcessing && (
              <GracePeriodExpiredBanner type="info">
                <FormattedMessage id={`${ns}.changePayMethodPeriodPassed`} />
              </GracePeriodExpiredBanner>
            )}
          </ModalContent>

          {renderActions({
            onCancel: handleCancel,
            onConfirm: handleConfirm,
            isConfirmDisabled,
          })}
        </ModalWrapper>

        {addPaymentMethodModal}
      </>
    )
  }

  // Legacy (FF off)
  return (
    <>
      <StyledModal
        appearance="sidebar"
        closeModal={handleCancel}
        data-testid="payment-method-selection-modal"
      >
        <Modal.Header>
          <FormattedMessage id={`${ns}.header`} />
        </Modal.Header>

        <LegacyModalContent>
          {showAchUnavailable && (
            <Banner type="info" hideIcon>
              <FormattedMessage id="BlockedAchPicker.message" />
            </Banner>
          )}
          {achProcessingBanner}
          {isFeeCardOnly ? (
            feeCardList
          ) : (
            <AutoPaymentMethodList {...sharedListProps} />
          )}
          <BalanceWidgetContainer id="balance-sdk" />
          {isAutoPayProcessing && (
            <GracePeriodExpiredBanner type="info">
              <FormattedMessage id={`${ns}.changePayMethodPeriodPassed`} />
            </GracePeriodExpiredBanner>
          )}
        </LegacyModalContent>

        <Modal.Actions alignment="firstOnLeft">
          <Button
            appearance="secondary"
            size="large"
            onClick={handleCancel}
            type="button"
          >
            <FormattedMessage id={`${ns}.cancelButton.label`} />
          </Button>
          <Button
            appearance="primary"
            size="large"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            type="button"
          >
            <FormattedMessage id={`${ns}.confirmButton.label`} />
          </Button>
        </Modal.Actions>
      </StyledModal>

      {addPaymentMethodModal}
    </>
  )
}
